import React, { useEffect, useState } from "react";
import { api } from "../lib/api";
import { toast } from "sonner";
import {
  Send, CheckCircle2, Search, RefreshCw, Mail, MailWarning, Power, AlertTriangle, Plus, Check, Trash2, Merge,
} from "lucide-react";
import MergeDuplicatesPanel, { mergeContracts } from "../components/activation/MergeDuplicates";

const DISC_LABELS = {
  muay_thai_kickboxing: "🥊 Kickboxing / Muay Thaï",
  muay_thai: "🥊 Kickboxing / Muay Thaï",
  kickboxing: "🥊 Kickboxing / Muay Thaï",
  power_kicker: "💃 PowerKick HER",
  powerkick_her: "💃 PowerKick HER",
  jiu_jitsu_bresilien: "🥋 BJJ",
  bjj: "🥋 BJJ",
  jujutsu_japonais: "🥋 Jiu-jitsu Can-Ryu",
  jiu_jitsu_can_ryu: "🥋 Jiu-jitsu Can-Ryu",
  petits_samourais: "👦 Petits Samouraïs",
  autre: "Autre",
};

const GROUPS = [
  { key: "actifs", label: "✅ Actifs", statuts: ["active", "actif"], accent: "#2A9D8F" },
  { key: "essai", label: "🟡 En essai", statuts: ["essai"], accent: "#BF8C00" },
  { key: "suspendus", label: "⏸️ Suspendus", statuts: ["suspendu"], accent: "#E76F51" },
  { key: "annules", label: "❌ Annulés", statuts: ["annule", "annulé", "cancelled"], accent: "#9b2226" },
  { key: "autres", label: "📋 Autres", statuts: ["a_reviser"], accent: "#6b7280" },
];

export default function MigrationActivationModule() {
  const [data, setData] = useState(null);
  const [search, setSearch] = useState("");
  const [programme, setProgramme] = useState("");  // '' | 'ps' | 'pkh' | 'academie'
  const PROG = { ps: (m) => ["petits_samourais", "kids_4_7"].includes(m.discipline), pkh: (m) => ["power_kicker", "powerkick_her"].includes(m.discipline), academie: (m) => !["petits_samourais", "kids_4_7", "power_kicker", "powerkick_her"].includes(m.discipline) };
  const [sending, setSending] = useState({});
  const [bulkBusy, setBulkBusy] = useState({});
  const [emailEdits, setEmailEdits] = useState({});
  const [selected, setSelected] = useState(new Set());
  const [merging, setMerging] = useState(false);

  const base = () => window.location.origin;

  const load = async () => {
    try {
      const r = await api.get("/adhesion/migration/members");
      setData(r.data);
    } catch {
      toast.error("Erreur de chargement des clients");
      setData({ members: [], groups: {}, total: 0, links_sent: 0, activated: 0, no_email: 0 });
    }
  };
  useEffect(() => { load(); }, []);

  async function sendTest() {
    try {
      const r = await api.post("/adhesion/migration/send-test", { base_url: base() });
      toast.success(`Courriel test envoyé à ${r.data.sent_to}.`);
    } catch (e) { toast.error(e?.response?.data?.detail || "Échec du test"); }
  }

  async function bulkSend(group) {
    const remaining = (data?.groups?.[group.key] || []).filter((m) => m.has_email && !m.activated && !m.migration_link_sent_at).length;
    if (remaining === 0) return toast.info("Aucun client non contacté (avec courriel) dans ce groupe.");
    if (!window.confirm(`Envoyer le lien d'activation à ${remaining} client(s) « ${group.label} » non contactés ? (envoi RÉEL)`)) return;
    setBulkBusy((s) => ({ ...s, [group.key]: true }));
    try {
      const r = await api.post("/adhesion/migration/send-all", { base_url: base(), statuts: group.statuts });
      toast.success(`${r.data.sent} lien(s) envoyé(s)${r.data.failed ? ` · ${r.data.failed} échec(s)` : ""}`);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Envoi groupé échoué");
    }
    setBulkBusy((s) => ({ ...s, [group.key]: false }));
  }

  async function sendLink(m) {
    if (!m.has_email) return toast.error("Ce client n'a pas de courriel");
    setSending((s) => ({ ...s, [m.id]: true }));
    try {
      await api.post("/adhesion/migration/send-link", { contract_id: m.id, base_url: base() });
      toast.success(`Lien d'activation envoyé à ${m.full_name}`);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Envoi échoué");
    }
    setSending((s) => ({ ...s, [m.id]: false }));
  }

  async function saveEmail(m) {
    const email = (emailEdits[m.id] || "").trim();
    if (!email || !email.includes("@")) return toast.error("Courriel invalide");
    try {
      await api.post("/adhesion/migration/set-email", { contract_id: m.id, email });
      toast.success("Courriel enregistré");
      setEmailEdits((s) => { const n = { ...s }; delete n[m.id]; return n; });
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Échec"); }
  }

  async function confirmActivation(m) {
    if (!window.confirm(`Confirmer manuellement que « ${m.full_name} » est activé ? (ex : payé en personne / virement)`)) return;
    setSending((s) => ({ ...s, [m.id]: true }));
    try {
      await api.post("/adhesion/migration/confirm", { contract_id: m.id });
      toast.success(`${m.full_name} marqué comme activé ✓`);
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Échec"); }
    setSending((s) => ({ ...s, [m.id]: false }));
  }

  async function unconfirmActivation(m) {
    if (!window.confirm(`Annuler la confirmation manuelle de « ${m.full_name} » ?`)) return;
    try {
      await api.post("/adhesion/migration/unconfirm", { contract_id: m.id });
      toast.success("Confirmation annulée");
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Échec"); }
  }

  async function deleteClient(m) {
    if (!window.confirm(`SUPPRIMER définitivement « ${m.full_name} » de la migration ? Cette action est irréversible.`)) return;
    try {
      await api.delete(`/adhesion/migration/${m.id}`);
      toast.success(`${m.full_name} supprimé`);
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Échec de la suppression"); }
  }

  if (!data) return <div className="p-8 text-zinc-400">Chargement…</div>;

  const allMembers = Object.values(data.groups || {}).flat();
  const selectedRows = allMembers.filter((m) => selected.has(m.id));
  const toggleSelect = (id) => setSelected((s) => { const n = new Set(s); n.has(id) ? n.delete(id) : n.add(id); return n; });
  async function mergeSelected() {
    if (selectedRows.length < 2) return;
    setMerging(true);
    try {
      const res = await mergeContracts(selectedRows, { allowDifferentNames: true });
      if (res) { toast.success(`${res.merged.length} doublon(s) fusionné(s) dans une seule fiche`); setSelected(new Set()); load(); }
    } catch (e) { toast.error(e?.response?.data?.detail || "Fusion refusée"); }
    finally { setMerging(false); }
  }

  const q = search.trim().toLowerCase();
  const matchSearch = (m) => (!programme || PROG[programme](m)) && (!q || (m.full_name || "").toLowerCase().includes(q) || (m.email || "").toLowerCase().includes(q));

  return (
    <div className="p-6 md:p-8 max-w-6xl mx-auto" data-testid="migration-activation-module">
      <h1 className="text-2xl font-bold text-zinc-900">🥋 Activation des clients — fermer Zen Planner</h1>
      <p className="text-sm text-zinc-500 mt-1 max-w-3xl">
        Tous tes clients, regroupés par statut. Envoie le lien d'activation (par Resend) à chacun ou par groupe.
        Le client choisit sa discipline + fréquence et démarre son abonnement aux 4 semaines.
      </p>

      {/* Compteurs */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mt-6">
        <Stat label="Total clients" value={data.total} testId="stat-total" />
        <Stat label="Liens envoyés" value={data.links_sent} testId="stat-sent" accent="#BF8C00" />
        <Stat label="Abonnements activés" value={data.activated} testId="stat-activated" accent="#2A9D8F" />
        <Stat label="Sans courriel" value={data.no_email} testId="stat-no-email" accent="#E76F51" />
      </div>

      {/* Barre de progression migration */}
      <div className="mt-5 border border-zinc-200 rounded-xl p-5 bg-white" data-testid="migration-progress">
        <div className="flex items-center justify-between mb-2">
          <span className="text-sm font-bold text-zinc-800">🏁 Migration Zen Planner</span>
          <span className="text-sm font-bold" style={{ color: "#2A9D8F" }} data-testid="migration-progress-label">
            {data.activated} / {data.total} activés
          </span>
        </div>
        <div className="w-full h-3 rounded-full bg-zinc-100 overflow-hidden">
          <div className="h-full rounded-full transition-all duration-700"
            style={{ width: `${data.total ? Math.round((data.activated / data.total) * 100) : 0}%`,
                     background: "linear-gradient(90deg,#2A9D8F,#1f7a6f)" }}
            data-testid="migration-progress-bar" />
        </div>
        <p className="text-xs text-zinc-500 mt-2">
          {data.total - data.activated} client(s) restant(s) à convertir
          {data.total ? ` · ${Math.round((data.activated / data.total) * 100)}% complété` : ""}.
          {" "}Objectif : 100 % pour fermer Zen Planner. 🥋
        </p>
      </div>

      {/* Recherche + test */}
      <div className="mt-6 flex items-center gap-3 flex-wrap">
        <select value={programme} onChange={(e) => setProgramme(e.target.value)} className="border border-zinc-300 rounded-lg px-3 py-2 text-sm bg-white" data-testid="activation-programme-filter">
          <option value="">Tous les programmes</option>
          <option value="academie">Académie (Kick/Muay Thaï · Can Ryu · BJJ)</option>
          <option value="ps">🥋 Petits Samouraïs (3-6 ans)</option>
          <option value="pkh">🥊 PowerKick Her</option>
        </select>
        <div className="flex items-center gap-2 border border-zinc-300 rounded-lg px-3 py-2 bg-white max-w-md flex-1 min-w-[220px]">
          <Search size={16} className="text-zinc-400" />
          <input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Rechercher un client…"
            data-testid="migration-search" className="flex-1 outline-none text-sm text-zinc-800 bg-transparent" />
        </div>
        <button onClick={sendTest} data-testid="send-test-btn"
          className="inline-flex items-center gap-2 border border-zinc-300 hover:bg-zinc-50 text-zinc-800 text-sm font-semibold px-4 py-2.5 rounded-lg transition-colors">
          <Mail size={15} /> Test à moi-même
        </button>
        <button onClick={load} data-testid="reload-btn"
          className="inline-flex items-center gap-2 border border-zinc-300 hover:bg-zinc-50 text-zinc-800 text-sm font-semibold px-3 py-2.5 rounded-lg">
          <RefreshCw size={15} /> Actualiser
        </button>
      </div>
      <div className="mt-2 flex items-start gap-2 text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded-lg p-2.5 max-w-3xl">
        <AlertTriangle size={14} className="mt-0.5 shrink-0" />
        <span>Les courriels partent <strong>réellement</strong> via Resend. Une relance automatique est envoyée après 5 jours aux clients non activés.</span>
      </div>

      <MergeDuplicatesPanel onMerged={load} />
      {selectedRows.length >= 2 && (
        <div className="sticky top-2 z-20 mt-4 flex items-center justify-between gap-3 flex-wrap bg-violet-700 text-white rounded-xl px-4 py-3 shadow-lg" data-testid="merge-selection-bar">
          <span className="text-sm font-semibold">{selectedRows.length} fiches cochées : {selectedRows.map((m) => m.full_name).join(" + ")}</span>
          <div className="flex gap-2">
            <button onClick={() => setSelected(new Set())} data-testid="merge-selection-clear" className="text-xs underline">Tout décocher</button>
            <button onClick={mergeSelected} disabled={merging} data-testid="merge-selected-btn"
              className="inline-flex items-center gap-1.5 bg-white text-violet-800 hover:bg-violet-50 disabled:opacity-50 text-xs font-bold px-3 py-1.5 rounded-lg">
              <Merge size={13} /> {merging ? "Fusion…" : "Fusionner en une seule fiche"}
            </button>
          </div>
        </div>
      )}

      <MergeDuplicatesPanel onMerged={load} />
      {selectedRows.length >= 2 && (
        <div className="sticky top-2 z-20 mt-4 flex items-center justify-between gap-3 flex-wrap bg-violet-700 text-white rounded-xl px-4 py-3 shadow-lg" data-testid="merge-selection-bar">
          <span className="text-sm font-semibold">{selectedRows.length} fiches cochées : {selectedRows.map((m) => m.full_name).join(" + ")}</span>
          <div className="flex gap-2">
            <button onClick={() => setSelected(new Set())} data-testid="merge-selection-clear" className="text-xs underline">Tout décocher</button>
            <button onClick={mergeSelected} disabled={merging} data-testid="merge-selected-btn"
              className="inline-flex items-center gap-1.5 bg-white text-violet-800 hover:bg-violet-50 disabled:opacity-50 text-xs font-bold px-3 py-1.5 rounded-lg">
              <Merge size={13} /> {merging ? "Fusion…" : "Fusionner en une seule fiche"}
            </button>
          </div>
        </div>
      )}

      {/* Groupes */}
      {GROUPS.map((group) => {
        const members = (data.groups?.[group.key] || []).filter(matchSearch);
        if (members.length === 0) return null;
        const remaining = members.filter((m) => m.has_email && !m.activated && !m.migration_link_sent_at).length;
        return (
          <div key={group.key} className="mt-8" data-testid={`group-${group.key}`}>
            <div className="flex items-center justify-between flex-wrap gap-2">
              <h2 className="text-lg font-bold text-zinc-800">{group.label}
                <span className="ml-2 text-sm font-normal text-zinc-400">({members.length})</span>
              </h2>
              {remaining > 0 && (
                <button onClick={() => bulkSend(group)} disabled={bulkBusy[group.key]} data-testid={`bulk-send-${group.key}`}
                  className="inline-flex items-center gap-2 text-white text-sm font-semibold px-4 py-2 rounded-lg transition-colors disabled:opacity-50"
                  style={{ backgroundColor: group.accent }}>
                  <Send size={14} /> {bulkBusy[group.key] ? "Envoi…" : `Envoyer aux ${remaining} non contactés`}
                </button>
              )}
            </div>
            <div className="mt-3 border border-zinc-200 rounded-lg overflow-hidden bg-white">
              <table className="w-full text-sm" data-testid={`members-list-${group.key}`}>
                <thead>
                  <tr className="text-left text-zinc-500 bg-zinc-50 border-b border-zinc-200">
                    <th className="py-3 px-4">Client</th>
                    <th className="py-3 px-4">Discipline</th>
                    <th className="py-3 px-4">Activation</th>
                    <th className="py-3 px-4 text-right">Action</th>
                  </tr>
                </thead>
                <tbody>
                  {members.map((m) => (
                    <tr key={m.id} className="border-b border-zinc-100" data-testid={`migration-row-${m.id}`}>
                      <td className="py-3 px-4">
                        <div className="flex items-start gap-2">
                          <input type="checkbox" checked={selected.has(m.id)} onChange={() => toggleSelect(m.id)} title="Cocher pour fusionner un doublon"
                            data-testid={`merge-check-${m.id}`} className="mt-1 accent-violet-700" />
                          <div>
                        <div className="font-medium text-zinc-900">{m.full_name}</div>
                        {m.has_email ? (
                          <div className="text-xs text-zinc-500">{m.email}</div>
                        ) : (
                          <div className="flex items-center gap-1.5 mt-1">
                            <MailWarning size={13} className="text-[#E76F51]" />
                            <input value={emailEdits[m.id] || ""} onChange={(e) => setEmailEdits((s) => ({ ...s, [m.id]: e.target.value }))}
                              placeholder="ajouter un courriel…" data-testid={`email-input-${m.id}`}
                              className="text-xs border border-zinc-300 rounded px-2 py-1 outline-none w-44" />
                            <button onClick={() => saveEmail(m)} data-testid={`save-email-${m.id}`}
                              className="text-xs bg-zinc-900 text-white px-2 py-1 rounded inline-flex items-center gap-1"><Plus size={11} /> OK</button>
                          </div>
                        )}
                          </div>
                        </div>
                      </td>
                      <td className="py-3 px-4 text-zinc-600">{DISC_LABELS[m.discipline] || m.discipline || "—"}</td>
                      <td className="py-3 px-4">
                        {m.activated ? (
                          <div className="flex flex-col gap-0.5">
                            <span className="inline-flex items-center gap-1 text-xs text-[#2A9D8F] font-semibold">
                              <CheckCircle2 size={14} /> Activé{m.migration_confirmed_manually ? " (manuel)" : ""}
                            </span>
                            {m.migration_confirmed_manually && !m.stripe_subscription_id && (
                              <button onClick={() => unconfirmActivation(m)} data-testid={`unconfirm-${m.id}`}
                                className="text-[10px] text-zinc-400 hover:text-[#E76F51] text-left underline">annuler la confirmation</button>
                            )}
                          </div>
                        ) : m.migration_link_sent_at ? (
                          <div className="text-xs text-[#BF8C00]">
                            Lien envoyé le {(m.migration_link_sent_at || "").slice(0, 10)}
                            {m.migration_reminder_sent_at && (
                              <span className="block text-[10px] text-zinc-400">Relancé le {(m.migration_reminder_sent_at || "").slice(0, 10)}</span>
                            )}
                          </div>
                        ) : (
                          <span className="text-xs text-zinc-400">Non envoyé</span>
                        )}
                      </td>
                      <td className="py-3 px-4">
                        <div className="flex items-center justify-end gap-1.5 flex-wrap">
                          {!m.activated && (
                            <button onClick={() => confirmActivation(m)} disabled={sending[m.id]}
                              data-testid={`confirm-${m.id}`}
                              className="inline-flex items-center gap-1.5 bg-[#2A9D8F] hover:bg-[#1f7a6f] disabled:opacity-40 text-white text-xs font-semibold px-3 py-1.5 rounded-lg transition-colors">
                              <Check size={13} /> Confirmer activé
                            </button>
                          )}
                          <button onClick={() => sendLink(m)} disabled={sending[m.id] || !m.has_email}
                            data-testid={`send-link-${m.id}`}
                            className="inline-flex items-center gap-1.5 bg-[#D90429] hover:bg-zinc-900 disabled:opacity-40 text-white text-xs font-semibold px-3 py-1.5 rounded-lg transition-colors">
                            {m.migration_link_sent_at ? <RefreshCw size={13} /> : <Send size={13} />}
                            {sending[m.id] ? "…" : m.migration_link_sent_at ? "Renvoyer" : "Envoyer le lien"}
                          </button>
                          {!m.stripe_subscription_id && (
                            <button onClick={() => deleteClient(m)} data-testid={`delete-${m.id}`}
                              title="Supprimer ce client"
                              className="inline-flex items-center justify-center border border-zinc-300 hover:border-[#E76F51] hover:text-[#E76F51] text-zinc-400 p-1.5 rounded-lg transition-colors">
                              <Trash2 size={14} />
                            </button>
                          )}
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        );
      })}
    </div>
  );
}

function Stat({ label, value, testId, accent }) {
  return (
    <div className="border border-zinc-200 rounded-lg p-4 bg-white">
      <div className="text-xs uppercase tracking-wide text-zinc-400">{label}</div>
      <div className="text-3xl font-bold mt-1" style={{ color: accent || "#18181b" }} data-testid={testId}>{value}</div>
    </div>
  );
}
